/**
 * Hub **hover hints**: portaled floating panels (not the browser `title` attribute). Product vocabulary — see
 * `docs/ui-style-guide.md` § **Hover hints (terminology)**.
 *
 * - **Hover hint with glyph (?):** **`HoverHintWithGlyph`**. **`PageTitleWithHelp`** / **`CardTitleWithHelp`** compose it next to headings.
 * - **Hover hint without glyph:**
 *   - **`HoverHintTrigger`** — anchor is the **`trigger`** you pass (explicit control).
 *   - **`HoverHintContent`** — anchor is the **`children`** you wrap (hit target is content; **`hoverHint`** is the portal body, usually a string, sometimes rich **`ReactNode`**).
 *
 * **`ReadOnlyHoverLayer`:** same panel style for explanatory hover over **disabled** controls.
 */
import type { CSSProperties, PropsWithChildren, ReactNode, RefObject } from "react";
import { createContext, useCallback, useContext, useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { HelpCircle } from "lucide-react";

import { cn } from "@/lib/utils";
import {
  HOVER_HINT_CLOSE_DELAY_MS,
  isNodeInsideForeignHoverHint,
  isNodeInsideHoverHintSurface,
} from "@/lib/hoverHintSurface";
import {
  mergeMeasuredPortaledHoverHintStyle,
  resolvePortaledHoverHintStyle,
} from "@/lib/portaledHoverHintStyle";
import { computeHoverHintFixedStyle, resolveHoverHintPortalZIndex } from "@/lib/viewportAnchoredPopover";

import { CardTitle } from "./Card";
import { HubBreadcrumbTitleRow } from "@/components/HubPageTitle";
import { resolvePageBreadcrumbLabel } from "@/lib/pageBreadcrumbLabel";
import { useNavigationTrail, usePageBreadcrumb } from "@/lib/navigationTrailContext";

/** Matches layout of `WorkOrdersPage` / `Dashboard` — no hover tint so the icon stays visually stable. */
const helpButtonClass =
  "inline-flex shrink-0 rounded p-0.5 text-slate-400 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-1";

const helpIconClass: Record<"sm" | "md", string> = {
  sm: "h-4 w-4",
  md: "h-5 w-5",
};

const portalHoverHintBodyClass =
  "pointer-events-auto w-max max-w-full overflow-visible whitespace-normal rounded-md border border-slate-200 bg-white px-2.5 py-2 text-left text-xs font-normal leading-snug text-slate-700 shadow-lg";

/** Inner body: shrink-wrap to copy. `block min-w-0` fills the 36rem panel cap. */
function portaledHoverHintInnerClass(content: ReactNode): string {
  const wrapLongCopy = typeof content === "string" || typeof content === "number";
  return wrapLongCopy ? "inline-block w-max max-w-full break-words" : "inline-block w-max max-w-full";
}

function portaledHoverHintPanelStyle(
  panelStyle: NonNullable<ReturnType<typeof computeHoverHintFixedStyle>>,
  portalZIndex: number,
): CSSProperties {
  return {
    position: "fixed",
    top: panelStyle.top,
    left: panelStyle.left,
    maxWidth: panelStyle.maxWidth,
    ...(panelStyle.maxHeight != null ? { maxHeight: panelStyle.maxHeight } : {}),
    transform: panelStyle.transform,
    zIndex: portalZIndex,
  };
}

function usePortaledHoverHintStyle(
  anchorRef: RefObject<HTMLElement | null>,
  panelRef: RefObject<HTMLElement | null>,
  open: boolean,
  align: "start" | "center" | "end",
  anchorRevision = 0,
): ReturnType<typeof computeHoverHintFixedStyle> | null {
  type HintStyle = ReturnType<typeof computeHoverHintFixedStyle>;
  const [positioned, setPositioned] = useState<{ revision: number; style: HintStyle } | null>(null);

  useLayoutEffect(() => {
    if (!open) {
      setPositioned(null);
    }
  }, [open]);

  const provisionalStyle =
    open && anchorRef.current
      ? computeHoverHintFixedStyle(anchorRef.current.getBoundingClientRect(), align, { contentHeight: null })
      : null;

  const reposition = useCallback(() => {
    const anchor = anchorRef.current;
    if (!anchor) {
      return;
    }
    const panel = panelRef.current;
    const contentHeight =
      panel !== null && panel.scrollHeight > 0 ? Math.ceil(panel.scrollHeight) : null;
    // Prefer scrollWidth so overflow past the soft max-width (wide tables) expands the shell.
    // Fall back to the border-box width when scrollWidth is not yet laid out.
    const panelRect = panel?.getBoundingClientRect();
    const scrollWidth = panel != null && panel.scrollWidth > 0 ? Math.ceil(panel.scrollWidth) : null;
    const borderWidth = panelRect != null && panelRect.width > 0 ? Math.ceil(panelRect.width) : null;
    const contentWidth =
      scrollWidth != null && borderWidth != null
        ? Math.max(scrollWidth, borderWidth)
        : (scrollWidth ?? borderWidth);
    const next = computeHoverHintFixedStyle(anchor.getBoundingClientRect(), align, {
      contentHeight,
      contentWidth,
    });
    setPositioned((previous) =>
      mergeMeasuredPortaledHoverHintStyle({
        anchorRevision,
        previous,
        nextStyle: next,
      }),
    );
  }, [align, anchorRevision]);

  useLayoutEffect(() => {
    if (!open) {
      return;
    }

    reposition();
    const frame = requestAnimationFrame(() => {
      reposition();
    });

    const panel = panelRef.current;
    const resizeObserver =
      panel && typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(() => {
            reposition();
          })
        : null;
    if (resizeObserver && panel) {
      resizeObserver.observe(panel);
    }

    window.addEventListener("resize", reposition);
    window.addEventListener("scroll", reposition, true);
    window.visualViewport?.addEventListener("resize", reposition);
    window.visualViewport?.addEventListener("scroll", reposition);

    return () => {
      cancelAnimationFrame(frame);
      resizeObserver?.disconnect();
      window.removeEventListener("resize", reposition);
      window.removeEventListener("scroll", reposition, true);
      window.visualViewport?.removeEventListener("resize", reposition);
      window.visualViewport?.removeEventListener("scroll", reposition);
    };
  }, [open, align, reposition, anchorRevision]);

  return resolvePortaledHoverHintStyle({
    open,
    anchorRevision,
    positioned,
    provisionalStyle,
  });
}

function documentBody(): HTMLElement | null {
  return typeof document !== "undefined" ? document.body : null;
}

const HoverHintRootIdContext = createContext<string | null>(null);

function useHoverHintTreeIds(): { rootId: string } {
  const instanceId = useId();
  const inheritedRoot = useContext(HoverHintRootIdContext);
  return { rootId: inheritedRoot ?? instanceId };
}

/**
 * When a dialog or other overlay covers the anchor, `pointerleave` on the anchor often does not run, so hover state
 * can stick open while the portaled panel (high z-index) stays visible.
 *
 * Panels are interactive: moving onto this panel (or a nested hover-hint panel in the same tree) must not dismiss.
 * Instant close on `pointermove` outside would close the panel in the gap between trigger and portal, so outside
 * pointer movement uses a short linger. Pointer onto a *different* hover hint closes immediately. Linger is armed
 * once (further movement must not keep resetting the timer). Click-away and Escape still close immediately.
 */
function useDismissPortaledHoverHintOnOutsideInteraction(
  anchorRef: RefObject<HTMLElement | null>,
  open: boolean,
  close: () => void,
  linger: () => void,
  rootId: string,
): void {
  useEffect(() => {
    if (!open) {
      return;
    }
    let dismissed = false;
    const safeClose = () => {
      if (dismissed) {
        return;
      }
      dismissed = true;
      close();
    };

    const isInsideSurface = (node: EventTarget | null): boolean => {
      return isNodeInsideHoverHintSurface(node, anchorRef.current, rootId);
    };

    const onPointerDownCapture = (event: PointerEvent) => {
      if (isInsideSurface(event.target)) {
        return;
      }
      safeClose();
    };
    const onPointerMoveCapture = (event: PointerEvent) => {
      if (isInsideSurface(event.target)) {
        return;
      }
      if (isNodeInsideForeignHoverHint(event.target, anchorRef.current, rootId)) {
        safeClose();
        return;
      }
      linger();
    };
    const onFocusInCapture = (event: FocusEvent) => {
      if (isInsideSurface(event.target)) {
        return;
      }
      linger();
    };
    const onKeyDownCapture = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        safeClose();
      }
    };

    document.addEventListener("pointerdown", onPointerDownCapture, true);
    document.addEventListener("pointermove", onPointerMoveCapture, true);
    document.addEventListener("focusin", onFocusInCapture, true);
    document.addEventListener("keydown", onKeyDownCapture, true);
    return () => {
      document.removeEventListener("pointerdown", onPointerDownCapture, true);
      document.removeEventListener("pointermove", onPointerMoveCapture, true);
      document.removeEventListener("focusin", onFocusInCapture, true);
      document.removeEventListener("keydown", onKeyDownCapture, true);
    };
  }, [open, anchorRef, close, linger, rootId]);
}

function useHoverHintOpenState(): {
  open: boolean;
  openFromPointer: () => void;
  lingerClose: () => void;
  setFocusInside: (inside: boolean) => void;
  closePanel: () => void;
} {
  const [hover, setHover] = useState(false);
  const [focusInside, setFocusInside] = useState(false);
  const closeTimerRef = useRef<number | null>(null);

  const clearCloseTimer = useCallback(() => {
    if (closeTimerRef.current != null) {
      window.clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  }, []);

  const openFromPointer = useCallback(() => {
    clearCloseTimer();
    setHover(true);
  }, [clearCloseTimer]);

  const lingerClose = useCallback(() => {
    if (closeTimerRef.current != null) {
      return;
    }
    closeTimerRef.current = window.setTimeout(() => {
      setHover(false);
      closeTimerRef.current = null;
    }, HOVER_HINT_CLOSE_DELAY_MS);
  }, []);

  const closePanel = useCallback(() => {
    clearCloseTimer();
    setHover(false);
    setFocusInside(false);
  }, [clearCloseTimer]);

  useEffect(() => {
    return () => {
      clearCloseTimer();
    };
  }, [clearCloseTimer]);

  return {
    open: hover || focusInside,
    openFromPointer,
    lingerClose,
    setFocusInside,
    closePanel,
  };
}

function HoverHintPortalPanel({
  panelRef,
  panelStyle,
  portalZIndex,
  rootId,
  children,
  onPointerEnter,
  onPointerLeave,
}: {
  panelRef: RefObject<HTMLSpanElement | null>;
  panelStyle: NonNullable<ReturnType<typeof computeHoverHintFixedStyle>>;
  portalZIndex: number;
  rootId: string;
  children: ReactNode;
  onPointerEnter: () => void;
  onPointerLeave: () => void;
}): JSX.Element {
  return (
    <span
      ref={panelRef}
      data-hub-hover-hint-panel=""
      data-hub-hover-hint-root={rootId}
      style={portaledHoverHintPanelStyle(panelStyle, portalZIndex)}
      className={portalHoverHintBodyClass}
      onPointerEnter={onPointerEnter}
      onPointerLeave={onPointerLeave}
    >
      <span className={portaledHoverHintInnerClass(children)}>{children}</span>
    </span>
  );
}

export type HoverHintTriggerProps = {
  /** Hover hint **without glyph**: explicit anchor. Usually a button with `aria-label`. */
  trigger: ReactNode;
  /** Panel body. Operators can move onto the panel and use links or other controls inside it. */
  children: ReactNode;
  align?: "start" | "center" | "end";
  className?: string;
  /** Fill anchor width (narrow grid cells) so `truncate` on the trigger works. */
  block?: boolean;
  /** Close the panel when the trigger is pressed or clicked (for example opening a callout from the anchor). */
  closeOnTriggerActivate?: boolean;
  /** Open the panel when its trigger is clicked or tapped. */
  openOnTriggerActivate?: boolean;
  /** Optional override. Hints inside `[data-hub-modal-overlay]` are raised automatically. */
  portalZIndex?: number;
};

/**
 * **Hover hint without glyph** — anchor on the **`trigger`** control. Opens on hover and on `focus-within` the trigger.
 * Portal positioning; see `docs/ui-style-guide.md` § Hover hints.
 */
export function HoverHintTrigger({
  trigger,
  children,
  align = "start",
  className,
  block = false,
  closeOnTriggerActivate = false,
  openOnTriggerActivate = false,
  portalZIndex,
}: HoverHintTriggerProps): JSX.Element {
  const wrapRef = useRef<HTMLSpanElement>(null);
  const panelRef = useRef<HTMLSpanElement>(null);
  const { rootId } = useHoverHintTreeIds();
  const { open, openFromPointer, lingerClose, setFocusInside, closePanel } = useHoverHintOpenState();
  useDismissPortaledHoverHintOnOutsideInteraction(wrapRef, open, closePanel, lingerClose, rootId);
  const panelStyle = usePortaledHoverHintStyle(wrapRef, panelRef, open, align);
  const target = documentBody();

  return (
    <HoverHintRootIdContext.Provider value={rootId}>
    <>
      <span
        ref={wrapRef}
        data-hub-hover-hint-anchor=""
        className={cn(
          "relative",
          block ? "flex min-w-0 w-full flex-col" : "inline-flex items-center",
          className,
        )}
        onPointerEnter={openFromPointer}
        onPointerLeave={lingerClose}
        onFocusCapture={() => setFocusInside(true)}
        onClick={
          openOnTriggerActivate
            ? () => {
                setFocusInside(true);
              }
            : undefined
        }
        onBlurCapture={(event) => {
          if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
            setFocusInside(false);
          }
        }}
      >
        <span
          className={cn(block ? "block min-w-0 w-full overflow-hidden" : "inline-flex")}
          onPointerDown={
            closeOnTriggerActivate
              ? () => {
                  closePanel();
                }
              : undefined
          }
        >
          {trigger}
        </span>
      </span>
      {open && panelStyle != null && target
        ? createPortal(
            <HoverHintPortalPanel
              panelRef={panelRef}
              panelStyle={panelStyle}
              portalZIndex={resolveHoverHintPortalZIndex(wrapRef.current, portalZIndex)}
              rootId={rootId}
              onPointerEnter={openFromPointer}
              onPointerLeave={lingerClose}
            >
              {children}
            </HoverHintPortalPanel>,
            target,
          )
        : null}
    </>
    </HoverHintRootIdContext.Provider>
  );
}

export type PortaledHoverHintProps = {
  open: boolean;
  anchorRef: RefObject<HTMLElement | null>;
  align?: "start" | "center" | "end";
  children: ReactNode;
  /** Increment when the anchor element changes while `open` stays true (shared grid tooltips). */
  anchorRevision?: number;
};

/**
 * Single portaled hover-hint panel anchored to an external element. Use when many hit targets share one tooltip
 * (for example forecast grid cells) instead of mounting {@link HoverHintContent} per cell.
 */
export function PortaledHoverHint({
  open,
  anchorRef,
  align = "start",
  children,
  anchorRevision = 0,
}: PortaledHoverHintProps): JSX.Element | null {
  const { rootId } = useHoverHintTreeIds();
  const panelRef = useRef<HTMLSpanElement>(null);
  const [panelHover, setPanelHover] = useState(false);
  const [linger, setLinger] = useState(false);
  const lingerTimerRef = useRef<number | null>(null);

  const clearLingerTimer = useCallback(() => {
    if (lingerTimerRef.current != null) {
      window.clearTimeout(lingerTimerRef.current);
      lingerTimerRef.current = null;
    }
  }, []);

  const armLinger = useCallback(() => {
    if (lingerTimerRef.current != null) {
      return;
    }
    setLinger(true);
    lingerTimerRef.current = window.setTimeout(() => {
      setLinger(false);
      setPanelHover(false);
      lingerTimerRef.current = null;
    }, HOVER_HINT_CLOSE_DELAY_MS);
  }, []);

  useEffect(() => {
    if (open) {
      clearLingerTimer();
      setLinger(false);
      return;
    }
    armLinger();
    return () => {
      clearLingerTimer();
    };
  }, [open, armLinger, clearLingerTimer]);

  useEffect(() => {
    return () => {
      clearLingerTimer();
    };
  }, [clearLingerTimer]);

  const show = open || panelHover || linger;
  const panelStyle = usePortaledHoverHintStyle(anchorRef, panelRef, show, align, anchorRevision);
  const target = documentBody();

  if (!show || panelStyle == null || !target) {
    return null;
  }

  return createPortal(
    <HoverHintRootIdContext.Provider value={rootId}>
    <HoverHintPortalPanel
      panelRef={panelRef}
      panelStyle={panelStyle}
      portalZIndex={resolveHoverHintPortalZIndex(anchorRef.current)}
      rootId={rootId}
      onPointerEnter={() => {
        clearLingerTimer();
        setLinger(false);
        setPanelHover(true);
      }}
      onPointerLeave={armLinger}
    >
      {children}
    </HoverHintPortalPanel>
    </HoverHintRootIdContext.Provider>,
    target,
  );
}

export type HoverHintContentProps = {
  /** Portal body: plain text or structured content (hover hint without glyph, content-anchored). */
  hoverHint: ReactNode;
  /** Becomes the hover/focus target (no separate ? icon). */
  children: ReactNode;
  align?: "start" | "center" | "end";
  className?: string;
  /** Fill width (table cells, truncated rows). */
  block?: boolean;
  /**
   * `help` shows the question-mark cursor on the wrapper. Use `default` for large hit targets (for example a full card)
   * so content reads as normal UI instead of a help surface.
   */
  cursor?: "help" | "default";
  /** Optional override. Hints inside `[data-hub-modal-overlay]` are raised automatically. */
  portalZIndex?: number;
  /**
   * On touch, the first tap on a nested link opens the panel instead of navigating; a second tap follows the link.
   * Use for {@link MrpSkuHoverPeek} and similar peek-through links on floor tablets.
   */
  openOnPointerActivate?: boolean;
};

/**
 * **Hover hint without glyph** — anchor on **wrapped children**; body is **`hoverHint`** only. Wrapper is `tabIndex={0}` for keyboard.
 * Prefer {@link HoverHintTrigger} when children include their own strongly focusable controls (avoid double tab stops).
 */
export function HoverHintContent({
  hoverHint,
  children,
  align = "start",
  className,
  block,
  cursor = "help",
  portalZIndex,
  openOnPointerActivate = false,
}: HoverHintContentProps): JSX.Element {
  const wrapRef = useRef<HTMLSpanElement>(null);
  const panelRef = useRef<HTMLSpanElement>(null);
  const { rootId } = useHoverHintTreeIds();
  const { open, openFromPointer, lingerClose, setFocusInside, closePanel } = useHoverHintOpenState();
  useDismissPortaledHoverHintOnOutsideInteraction(wrapRef, open, closePanel, lingerClose, rootId);
  const panelStyle = usePortaledHoverHintStyle(wrapRef, panelRef, open, align);
  const target = documentBody();

  return (
    <HoverHintRootIdContext.Provider value={rootId}>
    <>
      <span
        ref={wrapRef}
        data-hub-hover-hint-anchor=""
        className={cn(
          "relative outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-1 rounded-sm",
          cursor === "help" ? "cursor-help" : "cursor-default",
          block ? "flex min-w-0 flex-1 flex-col" : "inline-flex max-w-full items-center",
          className,
        )}
        tabIndex={openOnPointerActivate ? -1 : 0}
        onPointerEnter={openFromPointer}
        onPointerLeave={lingerClose}
        onPointerDownCapture={
          openOnPointerActivate
            ? (event) => {
                if (event.button !== 0) return;
                const link = (event.target as HTMLElement | null)?.closest("a[href]");
                if (link == null || !event.currentTarget.contains(link)) return;
                if (!open) {
                  event.preventDefault();
                  openFromPointer();
                  setFocusInside(true);
                }
              }
            : undefined
        }
        onFocus={() => setFocusInside(true)}
        onBlur={(event) => {
          if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
            setFocusInside(false);
          }
        }}
      >
        <span className={cn(block ? "min-w-0" : undefined, block && "flex min-w-0 flex-1 flex-col")}>{children}</span>
      </span>
      {open && panelStyle != null && target
        ? createPortal(
            <HoverHintPortalPanel
              panelRef={panelRef}
              panelStyle={panelStyle}
              portalZIndex={resolveHoverHintPortalZIndex(wrapRef.current, portalZIndex)}
              rootId={rootId}
              onPointerEnter={openFromPointer}
              onPointerLeave={lingerClose}
            >
              {hoverHint}
            </HoverHintPortalPanel>,
            target,
          )
        : null}
    </>
    </HoverHintRootIdContext.Provider>
  );
}

export type ReadOnlyHoverLayerProps = {
  /** When true, children are wrapped so a read-only message appears on hover (works with disabled controls). */
  active: boolean;
  hoverHint: string;
  children: ReactNode;
  className?: string;
  /** `inline`: icon buttons and pills. `block`: full-width sections (forms, fieldsets). */
  layout?: "inline" | "block";
};

/**
 * Replaces `title={readOnly ? hoverHint : undefined}` on wrappers around disabled hub controls. Uses a transparent
 * overlay so hover works even when a native `disabled` button would swallow pointer events.
 */
export function ReadOnlyHoverLayer({
  active,
  hoverHint,
  children,
  className,
  layout = "inline",
}: ReadOnlyHoverLayerProps): JSX.Element {
  const wrapRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLSpanElement>(null);
  const { rootId } = useHoverHintTreeIds();
  const [hover, setHover] = useState(false);
  const lingerTimerRef = useRef<number | null>(null);
  const clearLinger = useCallback(() => {
    if (lingerTimerRef.current != null) {
      window.clearTimeout(lingerTimerRef.current);
      lingerTimerRef.current = null;
    }
  }, []);
  const lingerClose = useCallback(() => {
    if (lingerTimerRef.current != null) {
      return;
    }
    lingerTimerRef.current = window.setTimeout(() => {
      setHover(false);
      lingerTimerRef.current = null;
    }, HOVER_HINT_CLOSE_DELAY_MS);
  }, []);
  const openFromPointer = useCallback(() => {
    clearLinger();
    setHover(true);
  }, [clearLinger]);
  const portalOpen = active && hover;
  const closePanel = useCallback(() => {
    clearLinger();
    setHover(false);
  }, [clearLinger]);
  useDismissPortaledHoverHintOnOutsideInteraction(wrapRef, portalOpen, closePanel, lingerClose, rootId);
  const panelStyle = usePortaledHoverHintStyle(wrapRef, panelRef, portalOpen, "start");
  const target = documentBody();

  useEffect(() => {
    if (!active) {
      setHover(false);
    }
  }, [active]);

  useEffect(() => {
    return () => {
      clearLinger();
    };
  }, [clearLinger]);

  if (!active) {
    return <>{children}</>;
  }
  const outer =
    layout === "block"
      ? "group/readonly relative flex w-full min-w-0 flex-col"
      : "group/readonly relative inline-flex max-w-full";
  const inner = layout === "block" ? "relative z-0 flex min-w-0 flex-1 flex-col" : "relative z-0 inline-flex max-w-full";

  return (
    <HoverHintRootIdContext.Provider value={rootId}>
    <>
      <div
        ref={wrapRef}
        data-hub-hover-hint-anchor=""
        className={cn(outer, className)}
        onPointerEnter={openFromPointer}
        onPointerLeave={lingerClose}
      >
        <span
          className="pointer-events-auto absolute inset-0 z-[1] cursor-not-allowed rounded-[inherit]"
          aria-hidden
        />
        <div className={inner}>{children}</div>
      </div>
      {portalOpen && panelStyle != null && target
        ? createPortal(
            <HoverHintPortalPanel
              panelRef={panelRef}
              panelStyle={panelStyle}
              portalZIndex={resolveHoverHintPortalZIndex(wrapRef.current)}
              rootId={rootId}
              onPointerEnter={openFromPointer}
              onPointerLeave={lingerClose}
            >
              {hoverHint}
            </HoverHintPortalPanel>,
            target,
          )
        : null}
    </>
    </HoverHintRootIdContext.Provider>
  );
}

export type HoverHintWithGlyphProps =
  | {
      /** Plain text for the panel and assistive label on the help button. */
      hoverHint: string;
      className?: string;
      size?: "sm" | "md";
      align?: "start" | "center" | "end";
      portalZIndex?: number;
      hoverHintContent?: never;
      hoverHintAriaLabel?: never;
    }
  | {
      /** Structured hover body; use `hoverHintAriaLabel` for the help button `aria-label`. */
      hoverHintContent: ReactNode;
      hoverHintAriaLabel: string;
      className?: string;
      size?: "sm" | "md";
      align?: "start" | "center" | "end";
      portalZIndex?: number;
      hoverHint?: never;
    };

/**
 * **Hover hint with glyph** — fixed `HelpCircle` button beside titles/labels. Built on {@link HoverHintTrigger}.
 * `tabIndex={-1}` so **Tab** skips the glyph; hover/focus-within opens the panel. See `docs/ui-style-guide.md` § Hover hints + Section titles.
 */
export function HoverHintWithGlyph(props: HoverHintWithGlyphProps): JSX.Element {
  const { className, size = "sm", align, portalZIndex } = props;
  const alignResolved = align ?? (size === "md" ? "center" : "start");
  if ("hoverHintContent" in props) {
    return (
      <HoverHintTrigger
        align={alignResolved}
        portalZIndex={portalZIndex}
        openOnTriggerActivate
        trigger={
          <button
            type="button"
            tabIndex={-1}
            className={cn(helpButtonClass, className)}
            aria-label={props.hoverHintAriaLabel}
          >
            <HelpCircle className={cn(helpIconClass[size], "shrink-0")} aria-hidden />
          </button>
        }
      >
        {props.hoverHintContent}
      </HoverHintTrigger>
    );
  }
  return (
    <HoverHintTrigger
      align={alignResolved}
      portalZIndex={portalZIndex}
      openOnTriggerActivate
      trigger={
        <button type="button" tabIndex={-1} className={cn(helpButtonClass, className)} aria-label={props.hoverHint}>
          <HelpCircle className={cn(helpIconClass[size], "shrink-0")} aria-hidden />
        </button>
      }
    >
      {props.hoverHint}
    </HoverHintTrigger>
  );
}

export function CardTitleWithHelp({
  children,
  hoverHint,
  className,
  titleClassName,
}: PropsWithChildren<{ hoverHint: string; className?: string; titleClassName?: string }>): JSX.Element {
  return (
    <div className={cn("flex flex-wrap items-center gap-1.5", className)}>
      <CardTitle className={cn("mb-0", titleClassName)}>{children}</CardTitle>
      <HoverHintWithGlyph hoverHint={hoverHint} />
    </div>
  );
}

export type PageTitleWithHelpProps = PropsWithChildren<{
  breadcrumbLabel?: string;
  className?: string;
  titleClassName?: string;
}> &
  (
    | { hoverHint: string; hoverHintContent?: never; hoverHintAriaLabel?: never }
    | { hoverHintContent: ReactNode; hoverHintAriaLabel: string; hoverHint?: never }
  );

export function PageTitleWithHelp(props: PageTitleWithHelpProps): JSX.Element {
  const { children, breadcrumbLabel, className, titleClassName } = props;
  const { trail } = useNavigationTrail();
  usePageBreadcrumb(resolvePageBreadcrumbLabel(breadcrumbLabel, children));

  let helpGlyph: JSX.Element;
  if (typeof props.hoverHint === "string") {
    helpGlyph = <HoverHintWithGlyph hoverHint={props.hoverHint} size="md" />;
  } else {
    helpGlyph = (
      <HoverHintWithGlyph
        hoverHintContent={props.hoverHintContent}
        hoverHintAriaLabel={props.hoverHintAriaLabel}
        size="md"
      />
    );
  }

  if (trail.length > 0) {
    return (
      <HubBreadcrumbTitleRow title={children} className={className} helpGlyph={helpGlyph} />
    );
  }

  return (
    <div className={cn("flex flex-wrap items-center gap-2", className)}>
      <h1 className={cn("text-xl font-semibold text-slate-900", titleClassName)}>{children}</h1>
      {helpGlyph}
    </div>
  );
}
